// src/pages/Studios/StudiosPage.tsx
import { Link } from 'react-router-dom';
import {
  Clapperboard,
  Handshake,
  Megaphone,
  Users,
  Camera,
  Mic,
  Scissors,
  PenLine,
  ArrowRight,
} from 'lucide-react';
import StudioShell from '../../components/Studio/StudioShell';
import StudioHero from '../../components/Studio/StudioHero';
import StudioSection from '../../components/Studio/StudioSection';
import ProductionCard from '../../components/Studio/ProductionCard';
import {
  StudioCallout,
  StudioEmptyState,
  StudioErrorState,
  StudioSkeleton,
} from '../../components/Studio/StudioStates';
import { CONTAINER, PANEL, PRIMARY_BUTTON, SECONDARY_BUTTON } from '../../components/Studio/studioStyles';
import useStudioResource from '../../hooks/useStudioResource';
import useSEO from '../../hooks/useSEO';
import communityService from '../../services/communityService';
import productionService from '../../services/productionService';

/** The eight things the studio does in-house, front to back. */
const CAPABILITIES = [
  {
    icon: PenLine,
    title: 'Development & writing',
    body: 'Original scripts written in-house, developed with the people who will perform them.',
  },
  {
    icon: Users,
    title: 'Casting from the community',
    body: 'We cast from our own repertory first. No agents, no gatekeepers, no cold submissions into a void.',
  },
  {
    icon: Camera,
    title: 'Production',
    body: 'Crewed, scheduled and shot by StrymHub members across Sheffield and Yorkshire.',
  },
  {
    icon: Scissors,
    title: 'Post & finishing',
    body: 'Edit, grade, sound and score — all handled by members of the troupe.',
  },
  {
    icon: Mic,
    title: 'Podcasts & audio',
    body: 'Long-form conversations with cast, crew and the wider independent scene.',
  },
  {
    icon: Clapperboard,
    title: 'Distribution',
    body: 'Everything premieres here. We own the work and we own the storefront.',
  },
  {
    icon: Handshake,
    title: 'Brand & sponsored work',
    body: 'Co-produced films and series for partners who want craft, not ad inventory.',
  },
  {
    icon: Megaphone,
    title: 'Building careers in public',
    body: 'Every member gets credited, gets a page, and builds a body of work that travels.',
  },
];

/** How a StrymHub production actually moves through the studio. */
const PIPELINE = [
  {
    step: '01',
    title: 'It starts as an idea',
    body: 'A script, a pitch, a scene someone wants to shoot. It gets a production page from day one.',
  },
  {
    step: '02',
    title: 'We open the call',
    body: 'Roles and crew positions go out to the community. Members apply directly — no agent required.',
  },
  {
    step: '03',
    title: 'We shoot it together',
    body: 'Cast and crew come from the troupe. Everyone is credited by name on the production page.',
  },
  {
    step: '04',
    title: 'It premieres here',
    body: 'The finished work, its trailer and its behind-the-scenes all live on one page. Free to watch.',
  },
];

const StudiosPage = () => {
  useSEO({
    title: 'StrymHub Studios — An Independent Production Company in Sheffield, UK',
    description:
      'StrymHub Studios writes, casts, shoots and finishes original films, series, animations and podcasts in-house, using a community-scouted repertory — and premieres everything on our own platform.',
    keywords:
      'strymhub studios, independent production company sheffield, indie film studio uk, in-house repertory, community casting, sheffield filmmakers, sponsored production, co-production',
    type: 'website',
  });

  const featured = useStudioResource((signal) => productionService.getFeatured(signal), []);
  const stats = useStudioResource((signal) => communityService.getStats(signal), []);

  const heroStats = stats.data
    ? [
        { label: 'Productions', value: String(stats.data.productions) },
        { label: 'In progress', value: String(stats.data.productionsInProgress) },
        { label: 'Members', value: String(stats.data.members) },
        { label: 'Open calls', value: String(stats.data.openCalls) },
      ]
    : undefined;

  return (
    <StudioShell>
      <StudioHero
        eyebrow="StrymHub Studios · Sheffield, UK"
        title="We don't licence films."
        accent="We make them."
        lede="StrymHub Studios is an independent production company. We write, cast, shoot and finish our own work with a repertory of members we build in public — then premiere all of it here, on the platform we own."
        stats={heroStats}
        actions={
          <>
            <Link to="/productions" className={PRIMARY_BUTTON}>
              See what we&apos;re making
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <Link to="/community/join" className={SECONDARY_BUTTON}>
              Join the troupe
            </Link>
          </>
        }
      />

      {/* What the studio does */}
      <StudioSection
        eyebrow="The studio"
        title="Everything under one roof"
        description="Most independent work dies in the gap between departments. We closed the gap by keeping all of it in-house — and by making the people who do it members, not freelancers we forget."
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {CAPABILITIES.map((capability) => (
            <div key={capability.title} className={`${PANEL} p-6`}>
              <capability.icon className="h-6 w-6 text-orange-400" aria-hidden="true" />
              <h3 className="mt-4 font-display text-base font-bold text-white">
                {capability.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-neutral-400">{capability.body}</p>
            </div>
          ))}
        </div>
      </StudioSection>

      {/* How a production runs */}
      <section className="border-y border-white/10 bg-white/[0.015] py-16 sm:py-20">
        <div className={CONTAINER}>
          <h2 className="heading-cinematic text-2xl text-white sm:text-3xl">
            How a StrymHub production runs
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-neutral-400 sm:text-base">
            Every production follows the same four steps, and every step is visible on the site
            while it happens. You can watch a film get made, not just watch the film.
          </p>

          <ol className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {PIPELINE.map((phase) => (
              <li key={phase.step} className="relative">
                <span className="font-display text-4xl font-extrabold text-orange-500/25">
                  {phase.step}
                </span>
                <h3 className="mt-3 font-display text-lg font-bold text-white">{phase.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-400">{phase.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Featured productions */}
      <StudioSection
        eyebrow="On the slate"
        title="Featured productions"
        description="In development, in production, or already premiered — every one of them made by people you can put a name and a face to."
        linkTo="/productions"
        linkLabel="All productions"
      >
        {featured.loading && <StudioSkeleton count={4} />}

        {!featured.loading && featured.error && (
          <StudioErrorState message={featured.error} onRetry={featured.reload} />
        )}

        {!featured.loading && !featured.error && (featured.data?.length ?? 0) === 0 && (
          <StudioEmptyState
            icon={<Clapperboard className="h-8 w-8" />}
            title="The first slate is being locked in"
            description="We're in development on our opening productions. Join the troupe now and you'll be in the room for the first casting call."
            action={
              <Link to="/community/join" className={PRIMARY_BUTTON}>
                Join the troupe
              </Link>
            }
          />
        )}

        {!featured.loading && !featured.error && (featured.data?.length ?? 0) > 0 && (
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
            {featured.data?.map((production) => (
              <ProductionCard key={production.id} production={production} />
            ))}
          </div>
        )}
      </StudioSection>

      {/* Partner CTA */}
      <div className={`${CONTAINER} pb-24`}>
        <StudioCallout
          title="Make something with us"
          description="Brands, theatre groups, festivals and arts organisations: we co-produce original work that carries your name because you helped make it — not because you bought a slot next to it."
          action={
            <Link to="/partners" className={PRIMARY_BUTTON}>
              Partner with the studio
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          }
        />
      </div>
    </StudioShell>
  );
};

export default StudiosPage;
